import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { NodeList } from '@/modules/folders/components/NodeList';
import { HomeCardGrid } from '../home/HomeSection';
import type { HomeView } from '../home/ViewSwitch';
import type { LibraryDocumentId } from './libraryApi';

export const LIBRARY_DOCUMENT_INFO = [
  { id: 'holidays', icon: 'calendar', titleKey: 'library.holidays.title', descriptionKey: 'library.holidays.description' },
  { id: 'exchange-rates', icon: 'cashflow', titleKey: 'library.exchangeRates.title', descriptionKey: 'library.exchangeRates.description' },
] as const satisfies readonly { id: LibraryDocumentId; icon: IconName; titleKey: string; descriptionKey: string }[];

/** Home's Numtable tab: the read-only reference documents every user shares. */
export function LibraryTab({ view }: { view: HomeView }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">{t('library.intro')}</p>
      {view === 'list' ? (
        <NodeList
          dateLabel=""
          rows={LIBRARY_DOCUMENT_INFO.map((doc) => ({
            // Not a Folders node: a stand-in row with its own link, no menu, no star.
            node: {
              id: `library:${doc.id}`, orgId: '', parentId: null, kind: 'document' as const, docType: null,
              name: t(doc.titleKey), color: null, trashedAt: null, updatedAt: '', createdBy: null,
            },
            actions: [],
            owner: 'Numtable',
            date: t(doc.descriptionKey),
            to: `/library/${doc.id}`,
            icon: doc.icon,
            typeLabel: t('library.readOnlyBadge'),
          }))}
        />
      ) : (
      <HomeCardGrid>
        {LIBRARY_DOCUMENT_INFO.map((doc) => (
          <li key={doc.id}>
            <LibraryDocumentCard id={doc.id} />
          </li>
        ))}
      </HomeCardGrid>
      )}
    </div>
  );
}

/** One library document as a Home card; `meta` replaces the Read-only line. */
export function LibraryDocumentCard({ id, meta }: { id: LibraryDocumentId; meta?: string }) {
  const { t } = useI18n();
  const doc = LIBRARY_DOCUMENT_INFO.find((d) => d.id === id)!;
  return (
            <Link
              to={`/library/${doc.id}`}
              className="flex h-full flex-col gap-2 rounded-token border border-border bg-surface p-4 hover:border-focus hover:bg-surface-hover"
            >
              <span className="flex items-center gap-2 font-medium text-fg">
                <Icon name={doc.icon} className="h-5 w-5 text-accent" />
                {t(doc.titleKey)}
              </span>
              <span className="text-xs text-muted">{t(doc.descriptionKey)}</span>
              <span className="mt-auto text-xs text-muted">{meta ?? t('library.readOnlyBadge')}</span>
            </Link>
  );
}
